import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import StateBlock from '../components/StateBlock';
import EntityLink from '../components/EntityLink';

// Ids added only to power EntityLink - kept out of the CSV (wo_id was
// already in the CSV before, so it stays).
const LINK_ID_COLS = new Set(['contractor_id', 'po_id', 'invoice_id']);

export default function DashboardDetailPage() {
  const [params] = useSearchParams();
  const type = params.get('type');
  const month_start = params.get('month_start');
  const month_end = params.get('month_end');
  const title = params.get('title') || 'Detail';

  const [rows, setRows] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!type || !month_start || !month_end) return;
    api.get('/dashboard-monthly/gaps', { params: { type, month_start, month_end } })
      .then(r => setRows(r.data.rows))
      .catch(() => setError(true));
  }, [type, month_start, month_end]);

  function fmt(n) {
    return `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
  }

  function downloadCsvClientSide() {
    if (!rows || !rows.length) return;
    const cols = Object.keys(rows[0]).filter(c => !LINK_ID_COLS.has(c));
    const csvRows = [cols.join(',')];
    rows.forEach(r => {
      csvRows.push(cols.map(c => {
        const val = r[c] == null ? '' : String(r[c]).replace(/"/g, '""');
        return `"${val}"`;
      }).join(','));
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${title.replace(/[^a-z0-9]+/gi, '_')}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  const isInvoiceType = rows && rows.length > 0 && 'invoice_number' in rows[0];
  const isActiveType = rows && rows.length > 0 && 'engagement_type' in rows[0];
  const invoiceLinkType = (type || '').startsWith('ap_') ? 'ap_invoice' : 'ar_invoice';

  return (
    <div style={{ padding: 28 }}>
      <div className="page-header">
        <div>
          <h1 className="page-title">{title}</h1>
          <p className="page-subtitle">{rows ? `${rows.length} result${rows.length === 1 ? '' : 's'}` : ''}</p>
        </div>
        {rows && rows.length > 0 && (
          <button className="btn btn-primary" onClick={downloadCsvClientSide}>↓ Download CSV</button>
        )}
      </div>

      {error && <StateBlock type="error" />}
      {!error && !rows && <StateBlock type="loading" />}
      {!error && rows && rows.length === 0 && (
        <StateBlock type="empty" title="Nothing here" message="No results found for this month." />
      )}
      {!error && rows && rows.length > 0 && (
        <table className="data-table">
          <thead>
            <tr>
              <th>Contractor</th><th>WO #</th>
              {!isInvoiceType && !isActiveType && <th>PO #</th>}
              {isInvoiceType && <><th>Invoice #</th><th>Amount</th><th>Paid</th><th>Outstanding</th></>}
              {isActiveType && <><th>Type</th><th>Status</th></>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.wo_id || r.invoice_number || i}>
                <td><EntityLink type="contractor" id={r.contractor_id}>{r.contractor}</EntityLink></td>
                <td><EntityLink type="work_order" id={r.wo_id}>{r.client_wo_number}</EntityLink></td>
                {!isInvoiceType && !isActiveType && <td><EntityLink type="purchase_order" id={r.po_id}>{r.po_number || '-'}</EntityLink></td>}
                {isInvoiceType && <>
                  <td><EntityLink type={invoiceLinkType} id={r.invoice_id}>{r.invoice_number}</EntityLink></td>
                  <td>{fmt(r.amount)}</td>
                  <td>{fmt(r.paid)}</td>
                  <td>{fmt(r.outstanding)}</td>
                </>}
                {isActiveType && <>
                  <td>{r.engagement_type === 'contract_placement' ? 'Contract' : 'Referral'}</td>
                  <td>{r.status}</td>
                </>}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
